export interface SelectOption {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  badge?: string;
  disabled?: boolean;
  vipOnly?: boolean;
}

interface SelectSheetProps {
  title: string;
  options: SelectOption[];
  selectedId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
}

export default function SelectSheet({ title, options, selectedId, isOpen, onClose, onSelect }: SelectSheetProps) {
  const handleSelect = useCallback((option: SelectOption) => {
    if (option.disabled || option.vipOnly) return;
    onSelect(option.id);
    onClose();
  }, [onSelect, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-3xl max-h-[70vh] overflow-hidden animate-slide-up">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="px-5 space-y-2" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
          {options.map((option) => {
            const isSelected = selectedId === option.id;
            const isLocked = option.vipOnly || option.disabled;
            return (
              <button
                key={option.id}
                data-testid={`select-option-${option.id}`}
                aria-label={`select-option-${option.id}`}
                onClick={() => handleSelect(option)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-2xl text-left transition-all cursor-pointer ${
                  isSelected ? 'bg-pink-50 ring-2 ring-pink-400' : 'bg-gray-50 hover:bg-gray-100'
                } ${isLocked ? 'opacity-60 cursor-not-allowed' : 'active:scale-[0.98]'}`}
              >
                {option.icon && (
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'text-pink-500' : 'text-gray-400'
                    }`}
                    style={{ background: isSelected ? 'rgba(255,107,157,0.1)' : '#F3F4F6' }}
                  >
                    <i className={`${option.icon} text-lg`} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${isSelected ? 'text-pink-600' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                    {option.badge && (
                      <span
                        className="px-1.5 py-0.5 rounded-md text-[10px] font-medium text-white whitespace-nowrap"
                        style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
                      >
                        {option.badge}
                      </span>
                    )}
                    {option.vipOnly && (
                      <i className="ri-vip-crown-2-line text-amber-500 text-sm" />
                    )}
                  </div>
                  {option.description && (
                    <p className="text-xs text-gray-400 mt-0.5">{option.description}</p>
                  )}
                </div>
                {isSelected && (
                  <i className="ri-check-line text-pink-500 text-lg" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
